import { AddLabOrderDialog } from '@/components/molecules/AddLabOrderDialog';
import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useInfiniteRecordList } from '@liza/ui/hooks/useInfiniteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import { HEALTH_GORILLA_ORDER_PROFILE_URL } from '@liza/fhir-utils';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { ServiceRequestsList } from '@liza/ui/components/records';
import {
  type RecordsListFilters,
  defaultFilters,
  RecordsListControls,
} from '@liza/ui/components/records/RecordsListControls';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedId?: string;
};

export const LabOrders = ({ onViewChange, patient, selectedId }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);

  // Filter to ServiceRequests carrying the HG order profile. This excludes HG sub-orders
  // (per-test child SRs that point at a parent via `basedOn`) — only parent orders carry
  // the profile, which is exactly the user-facing concept of "an order."
  const listOptions = {
    ownership: filters.ownership,
    searchParams: {
      'code:text': filters.searchQuery,
      _profile: HEALTH_GORILLA_ORDER_PROFILE_URL,
    },
    status: filters.status,
  };

  const {
    items: serviceRequests,
    query,
    bottomSentinelRef,
  } = useInfiniteRecordList({
    queryKey: trpc.patient.serviceRequest.list.queryKey({ medplumPatientId: patient.medplumId, options: listOptions }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.serviceRequest.list.query({
        medplumPatientId: patient.medplumId,
        options: listOptions,
        offset,
        count,
      });
      return { items: res.serviceRequests, hasMore: res.hasMore };
    },
  });

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('labOrders')}
        searchPlaceholder={t('searchLabOrders')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('createLabOrder')}
      />
      <AddLabOrderDialog medplumPatientId={patient.medplumId} isOpen={isAddOpen} onOpenChange={setIsAddOpen} />
      <RecordListScroll
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetchingNextPage={query.isFetchingNextPage}
        bottomSentinelRef={bottomSentinelRef}
      >
        <ServiceRequestsList
          serviceRequests={serviceRequests}
          onSelectServiceRequest={(id) => onViewChange({ type: 'lab-order-detail', id })}
          selectedServiceRequestId={selectedId}
        />
      </RecordListScroll>
    </div>
  );
};
